import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Send } from "lucide-react";

const inp = "px-2.5 py-1.5 rounded-md border text-sm";
const TRIG = { "lead.created": "Lead is created", "field.changed": "A field changes", "outcome.logged": "SOP outcome is logged" };
const OPL = { any: "changes to anything", is: "becomes", is_not: "becomes anything but", is_empty: "is cleared", not_empty: "gets any value" };
const BUILTIN = ["name", "email", "phone", "stage", "owner", "qualification", "gmv", "event_date", "source", "sop_id", "module"];

function Rule({ r, i, fields, ops, triggers, set, del, isAdmin }) {
  const [busy, setBusy] = useState(false);
  const test = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/event-hooks/test", r);
      data.ok ? toast.success(`Test sent (${data.status})`) : toast.error(`Test failed: ${data.status}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally { setBusy(false); }
  };
  const needsValue = ["is", "is_not"].includes(r.op);
  return (
    <div className="panel-2 rounded-lg p-3 space-y-2" data-testid={`wf-rule-${i}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <input type="checkbox" checked={r.enabled !== false} disabled={!isAdmin} onChange={(e) => set({ enabled: e.target.checked })} data-testid={`wf-rule-${i}-enabled`} />
        <input className={`${inp} flex-1 min-w-[160px] font-medium`} placeholder="Rule name, e.g. Qualified → Meta CAPI" value={r.name || ""} onChange={(e) => set({ name: e.target.value })} data-testid={`wf-rule-${i}-name`} />
        {isAdmin && <button className="btn text-xs" disabled={!r.url || busy} onClick={test} data-testid={`wf-rule-${i}-test`}><Send size={12} />{busy ? "Sending…" : "Send test"}</button>}
        {isAdmin && <button className="btn-ghost p-1 hover:text-[var(--breach)]" onClick={del} data-testid={`wf-rule-${i}-delete`}><Trash2 size={13} /></button>}
      </div>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="text-[var(--text-dim)]">When</span>
        <select className={inp} value={r.trigger} onChange={(e) => set({ trigger: e.target.value })} data-testid={`wf-rule-${i}-trigger`}>{triggers.map((t) => <option key={t} value={t}>{TRIG[t] || t}</option>)}</select>
        {r.trigger === "field.changed" ? <span className="text-[var(--text-dim)]">field</span> : <span className="text-[var(--text-dim)]">and field</span>}
        <input list="wf-fields" className={`${inp} w-44`} placeholder={r.trigger === "field.changed" ? "any field" : "(no condition)"} value={r.field || ""} onChange={(e) => set({ field: e.target.value })} data-testid={`wf-rule-${i}-field`} />
        {r.field && <select className={inp} value={r.op || "any"} onChange={(e) => set({ op: e.target.value })} data-testid={`wf-rule-${i}-op`}>{ops.map((o) => <option key={o} value={o}>{r.trigger === "field.changed" ? OPL[o] : OPL[o].replace("changes to anything", "has any value").replace("becomes", "is")}</option>)}</select>}
        {r.field && needsValue && <input className={`${inp} w-40`} placeholder="value" value={r.value || ""} onChange={(e) => set({ value: e.target.value })} data-testid={`wf-rule-${i}-value`} />}
      </div>
      <div className="flex items-center gap-2 text-sm">
        <span className="text-[var(--text-dim)]">POST to</span>
        <input className={`${inp} flex-1 font-mono`} placeholder="https://hooks.zapier.com/…" value={r.url || ""} onChange={(e) => set({ url: e.target.value })} data-testid={`wf-rule-${i}-url`} />
      </div>
      <div className="flex items-center gap-2 text-sm">
        <span className="text-[var(--text-dim)]">Send once per lead, lock field</span>
        <input list="wf-fields" className={`${inp} w-44`} placeholder="(none), e.g. ql_lock" value={r.lock_field || ""} onChange={(e) => set({ lock_field: e.target.value })} data-testid={`wf-rule-${i}-lock-field`} />
      </div>
      <datalist id="wf-fields">{fields.map((f) => <option key={f} value={f} />)}</datalist>
    </div>
  );
}

function Deliveries({ rows }) {
  if (!rows.length) return null;
  return (
    <div className="space-y-1" data-testid="wf-deliveries">
      <div className="section-label">Recent sends</div>
      {rows.slice(0, 10).map((r, i) => (
        <div key={i} className="text-xs flex gap-3 text-[var(--text-dim)]">
          <span className="w-28 shrink-0">{r.at?.slice(5, 16).replace("T", " ")}</span>
          <span className={r.ok ? "text-[var(--ok)]" : "text-[var(--breach)]"}>{String(r.status)}{r.attempts > 1 ? ` · ${r.attempts} tries` : ""}</span>
          <span className="truncate">{r.rule || r.event}{r.field ? ` · ${r.field}` : ""}{r.lead ? ` · ${r.lead}` : ""}</span>
        </div>
      ))}
    </div>
  );
}

export default function WorkflowHooks({ isAdmin }) {
  const [d, setD] = useState(null);
  const [fields, setFields] = useState(BUILTIN);
  const load = () => api.get("/event-hooks").then((r) => setD(r.data));
  useEffect(() => {
    load();
    api.get("/crm/config").then((r) => setFields([...new Set([...BUILTIN, ...(r.data.fields || []).map((f) => f.key), "utm.fbclid", "utm.utm_source", "utm.utm_campaign"])]));
  }, []);
  if (!d) return <div className="text-xs text-[var(--text-faint)]">Loading…</div>;
  const setRules = (rules) => setD({ ...d, rules });
  const save = async () => {
    try { await api.put("/event-hooks", d.rules); toast.success("Webhooks saved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  return (
    <div className="space-y-3 max-w-4xl" data-testid="workflow-hooks">
      <div className="text-xs text-[var(--text-dim)] leading-relaxed">Like Zoho workflow rules: when a lead is created, any field changes (from the lead page, SOP outcomes, bulk edits, Calendly) or an outcome is logged, we POST the lead as flat JSON (every field, <code className="kbd">qualified</code> true/false, fbclid, fbc, utm_*, event_time) plus <code className="kbd">field</code>, <code className="kbd">old_value</code>, <code className="kbd">new_value</code> and all <code className="kbd">changes</code>. Failed sends retry twice. Type any field key, including custom and form-answer fields.</div>
      {d.rules.map((r, i) => (
        <Rule key={r.id || i} r={r} i={i} fields={fields} ops={d.ops} triggers={d.triggers} isAdmin={isAdmin}
          set={(p) => setRules(d.rules.map((x, j) => (j === i ? { ...x, ...p } : x)))} del={() => setRules(d.rules.filter((_, j) => j !== i))} />
      ))}
      {isAdmin && (
        <div className="flex gap-2">
          <button className="btn text-xs" onClick={() => setRules([...d.rules, { name: "", trigger: "field.changed", field: "", op: "any", value: "", url: "", enabled: true }])} data-testid="wf-add-rule"><Plus size={12} />Add rule</button>
          <button className="btn btn-primary text-xs ml-auto" onClick={save} data-testid="wf-save">Save</button>
        </div>
      )}
      <Deliveries rows={d.recent} />
    </div>
  );
}
